import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { global } from "../../shared/libs/Global";

export type DeployNotificationEvent = {
  event_id: string;
  event_type: "PHASE_STARTED" | "PHASE_FINISHED";
  phase: "DB_SYNC" | "GIT_SYNC";
  run_id: string;
  no_issue: string;
  target_env: "STG" | "REL";
  requested_action: "TEST" | "CONF";
  status: "RUNNING" | "SUC" | "PART" | "FAIL" | "EMPTY";
  summary: string;
  occurred_at: string;
};

const EVENT_NAME = "deploy.notification";
const CONTEXT_PATH = "/snotes";
const MAX_DEDUPED_EVENTS = 200;

export function deployToastMessage(event: DeployNotificationEvent) {
  const phase = event.phase === "DB_SYNC" ? "DB Sync" : "Git Sync";
  const issue = event.no_issue ? `Issue ${event.no_issue}` : "Issue";
  const env = event.target_env ? ` [${event.target_env}]` : "";
  return `${issue}${env} ${phase}: ${event.summary}`;
}

export function showDeployToast(event: DeployNotificationEvent) {
  const message = deployToastMessage(event);
  if (event.status === "FAIL") {
    toast.error(message);
    return;
  }
  if (event.status === "PART") {
    toast.warning(message);
    return;
  }
  if (event.status === "SUC" || event.status === "EMPTY") {
    toast.success(message);
    return;
  }
  toast.info(message);
}

export default function DeployNotificationListener() {
  const userInfo = global((state) => state.userInfo);
  const shownEventIdsRef = useRef<string[]>([]);
  const shownEventIdSetRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userInfo?.cd_user) {
      return;
    }

    const tenantId = userInfo.tenant_id || "DEFAULT";
    const source = new EventSource(
      `${CONTEXT_PATH}/api/deploy/events?tenant_id=${encodeURIComponent(tenantId)}`,
      { withCredentials: true }
    );

    const handleDeployNotification = (message: MessageEvent<string>) => {
      try {
        const event = JSON.parse(message.data) as DeployNotificationEvent;
        if (!event.event_id || shownEventIdSetRef.current.has(event.event_id)) {
          return;
        }

        shownEventIdSetRef.current.add(event.event_id);
        shownEventIdsRef.current.push(event.event_id);
        while (shownEventIdsRef.current.length > MAX_DEDUPED_EVENTS) {
          const oldest = shownEventIdsRef.current.shift();
          if (oldest) {
            shownEventIdSetRef.current.delete(oldest);
          }
        }

        showDeployToast(event);
      } catch (error) {
        console.warn("deploy notification parse failed", error);
      }
    };

    source.addEventListener(
      EVENT_NAME,
      handleDeployNotification as EventListener
    );
    return () => {
      source.removeEventListener(
        EVENT_NAME,
        handleDeployNotification as EventListener
      );
      source.close();
    };
  }, [userInfo?.cd_user, userInfo?.tenant_id]);

  return null;
}
